import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Send, Zap, Phone, MapPin, Truck, Clock, Globe, BadgeCheck, Star } from "lucide-react";
import api, { apiErr } from "../lib/api";
import { COUNTRIES } from "../lib/countries";

const DAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

export function FlexSearch({ mode }) {
  const [cats, setCats] = useState([]);
  const [f, setF] = useState({ category: "", country: "", shift: "", max_wage: "", has_transport: false, q: "", days: [] });
  const [workers, setWorkers] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);

  const search = async () => {
    setLoading(true);
    try {
      const params = { mode };
      if (f.category) params.category = f.category;
      if (f.country) params.country = f.country;
      if (f.shift) params.shift = f.shift;
      if (f.max_wage) params.max_wage = f.max_wage;
      if (f.has_transport) params.has_transport = true;
      if (f.q) params.q = f.q;
      if (f.days.length) params.days = f.days.join(",");
      const r = await api.get("/employer/flex/search", { params });
      setWorkers(r.data.workers);
      setTotal(r.data.total);
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/flex/categories").then((r) => setCats(r.data[mode] || [])).catch(() => {});
    setF({ category: "", country: "", shift: "", max_wage: "", has_transport: false, q: "", days: [] });
    search();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode]);

  const toggleDay = (d) => setF((s) => ({ ...s, days: s.days.includes(d) ? s.days.filter((x) => x !== d) : [...s.days, d] }));
  const isRemote = mode === "remote";

  return (
    <div className="space-y-6 fade-up" data-testid={`flex-search-${mode}`}>
      <div className="border border-[#0A0A0A] p-5 space-y-4">
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-3">
          <select data-testid="flex-filter-category" value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">All categories</option>
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="flex-filter-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">All countries</option>
            {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="flex-filter-shift" value={f.shift} onChange={(e) => setF({ ...f, shift: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm">
            <option value="">Any shift</option>
            {["Day", "Evening", "Night", "Flexible"].map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <input data-testid="flex-filter-wage" type="number" placeholder="Max $/hr" value={f.max_wage} onChange={(e) => setF({ ...f, max_wage: e.target.value })} className="border border-[#0A0A0A] px-3 py-2 text-sm" />
          <input data-testid="flex-filter-q" placeholder={isRemote ? "Skill / keyword" : "City / skill"} value={f.q} onChange={(e) => setF({ ...f, q: e.target.value })} onKeyDown={(e) => e.key === "Enter" && search()} className="border border-[#0A0A0A] px-3 py-2 text-sm" />
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-mono2 text-xs uppercase text-[#4B5563]">Days:</span>
          {DAYS.map((d) => (
            <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`flex-filter-day-${d}`}
              className={`px-2.5 py-1 text-xs font-medium border ${f.days.includes(d) ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-[#0A0A0A]"}`}>{d}</button>
          ))}
          {!isRemote && (
            <label className="flex items-center gap-1.5 text-xs ml-1 cursor-pointer">
              <input type="checkbox" checked={f.has_transport} onChange={(e) => setF({ ...f, has_transport: e.target.checked })} className="accent-[#002FA7]" data-testid="flex-filter-transport" /> Has transport
            </label>
          )}
          <button data-testid="flex-search-btn" onClick={search} className="btn-ink px-4 py-1.5 text-xs font-medium ml-auto inline-flex items-center gap-1.5">
            <Send size={13} /> Search
          </button>
        </div>
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-[#4B5563]"><Loader2 className="animate-spin inline mr-2" size={16} /> Searching…</div>
      ) : workers.length === 0 ? (
        <div className="border-2 border-dashed border-[#E5E7EB] p-10 text-center text-sm text-[#4B5563]" data-testid="flex-empty">No {isRemote ? "remote" : "on-site"} workers match yet. Try widening your filters.</div>
      ) : (
        <>
          <p className="font-mono2 text-xs text-[#4B5563]">{total} worker{total === 1 ? "" : "s"} found</p>
          <div className="grid md:grid-cols-2 gap-4" data-testid="flex-results">
            {workers.map((w) => <FlexCard key={w.id} w={w} isRemote={isRemote} />)}
          </div>
        </>
      )}
    </div>
  );
}

function Stars({ value, size = 13 }) {
  return (
    <span className="inline-flex items-center gap-0.5" data-testid="flex-stars">
      {[1, 2, 3, 4, 5].map((n) => (
        <Star key={n} size={size} className={n <= Math.round(value) ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB]"} />
      ))}
    </span>
  );
}

function FlexCard({ w, isRemote }) {
  const blocked = (w.blocked_dates || []).length;
  const loc = [w.location, w.country].filter(Boolean).join(", ");
  const [rating, setRating] = useState(w.rating_avg || 0);
  const [count, setCount] = useState(w.rating_count || 0);
  const [open, setOpen] = useState(false);
  const [myStars, setMyStars] = useState(0);
  const [comment, setComment] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!myStars) { toast.error("Pick a star rating first."); return; }
    setSaving(true);
    try {
      await api.post(`/employer/flex/${w.id}/review`, { rating: myStars, comment });
      const nc = count + 1;
      setRating(Math.round(((rating * count) + myStars) / nc * 10) / 10);
      setCount(nc);
      setOpen(false);
      toast.success("Review submitted.");
    } catch (e) {
      toast.error(apiErr(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="border border-[#0A0A0A] p-5 space-y-3" data-testid={`flex-card-${w.id}`}>
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="font-display font-bold text-lg tracking-tight flex items-center gap-1.5">
            {w.name || "Flex worker"}
            {w.verified && <BadgeCheck size={16} className="text-[#10B981]" data-testid="flex-verified-marker" title="Verified worker" />}
          </div>
          <div className="text-xs text-[#4B5563] flex items-center gap-1 mt-0.5">
            {isRemote ? <><Globe size={12} /> Remote{w.country ? ` · ${w.country}` : ""}</> : <><MapPin size={12} /> {loc || "—"}</>}
            {!isRemote && w.has_transport && <span className="ml-2 inline-flex items-center gap-1 text-[#10B981]"><Truck size={12} /> Own transport</span>}
            {w.work_mode === "both" && <span className="ml-2 font-mono2 text-[10px] uppercase text-[#002FA7]">On-site + Remote</span>}
          </div>
          {count > 0 && (
            <div className="flex items-center gap-1.5 mt-1.5" data-testid="flex-card-rating">
              <Stars value={rating} />
              <span className="font-mono2 text-xs text-[#4B5563]">{rating} ({count})</span>
            </div>
          )}
        </div>
        <div className="font-display font-black text-2xl tracking-tighter text-[#002FA7]" data-testid="flex-card-score">{w.match_score}%</div>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {(w.categories || []).map((c) => <span key={c} className="font-mono2 text-[10px] uppercase bg-[#F3F4F6] border border-[#E5E7EB] px-2 py-0.5">{c}</span>)}
      </div>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-sm">
        {w.desired_hourly_wage != null && <span className="font-medium">${w.desired_hourly_wage}/hr</span>}
        {w.shift && <span className="text-[#4B5563] inline-flex items-center gap-1"><Clock size={13} /> {w.shift}</span>}
        {(w.days || []).length > 0 && <span className="font-mono2 text-xs text-[#4B5563]">{w.days.join(" · ")}</span>}
      </div>
      {blocked > 0 && <div className="font-mono2 text-xs text-[#D97706]">{blocked} date{blocked === 1 ? "" : "s"} blocked off</div>}
      {w.skills?.length > 0 && <div className="text-xs text-[#4B5563]">Skills: {w.skills.join(", ")}</div>}
      {w.summary && <p className="text-sm text-[#4B5563] line-clamp-2">{w.summary}</p>}

      {open && (
        <div className="border border-[#E5E7EB] p-3 space-y-2" data-testid="flex-review-form">
          <div className="flex items-center gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button key={n} type="button" data-testid={`flex-review-star-${n}`} onClick={() => setMyStars(n)}>
                <Star size={22} className={n <= myStars ? "fill-[#F59E0B] text-[#F59E0B]" : "text-[#D1D5DB] hover:text-[#F59E0B]"} />
              </button>
            ))}
          </div>
          <textarea data-testid="flex-review-comment" value={comment} onChange={(e) => setComment(e.target.value)} rows={2} placeholder="Optional comment…" className="w-full border border-[#0A0A0A] px-2.5 py-1.5 text-sm focus:outline-none focus:ring-1 focus:ring-[#002FA7]" />
          <div className="flex gap-2">
            <button data-testid="flex-review-submit" onClick={submit} disabled={saving} className="btn-ink px-3 py-1.5 text-xs font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <Loader2 size={13} className="animate-spin" /> : <Star size={13} />} Submit
            </button>
            <button onClick={() => setOpen(false)} className="btn-outline px-3 py-1.5 text-xs font-medium">Cancel</button>
          </div>
        </div>
      )}

      <div className="flex gap-2">
        <a href={`tel:${w.phone}`} data-testid="flex-card-call" className="btn-ink flex-1 py-2 text-sm font-medium inline-flex items-center justify-center gap-2">
          <Phone size={15} /> {w.phone || "No phone"}
        </a>
        <button data-testid="flex-rate-btn" onClick={() => setOpen((o) => !o)} className="btn-outline px-3 py-2 text-sm font-medium inline-flex items-center gap-1.5">
          <Star size={15} /> Rate
        </button>
      </div>
    </div>
  );
}
